import { ClickAwayListener } from "@mui/material";
import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import DotsReorderIcon from "@redotech/redo-icons/dots-reorder.svg";
import classnames from "classnames";
import Fuse from "fuse.js";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import DraggableList from "react-draggable-list";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "./arbiter-components/checkbox/redo-checkbox";
import { BaseRedoInput } from "./arbiter-components/input/base-redo-text-input";
import * as styles from "./column-selector.module.css";
import { Divider } from "./divider";
import { Dropdown } from "./dropdown";
import { Flex } from "./flex";
import { Column2 } from "./table2";
import { Text } from "./text";

interface ColumnItemProps<T> {
  item: Column2<T>;
  commonProps: { onSelect: (selected: boolean, key: string) => void };
  dragHandleProps: any;
  showDragHandle?: boolean;
}

export const ColumnSelector = genericMemo(function ColumnSelection<T>({
  columns,
  dropdownButtonRef,
  displayedColumns,
  setDisplayedColumns,
}: {
  columns: Column2<T>[];
  dropdownButtonRef: HTMLButtonElement | null;
  displayedColumns: { key: string; displayed: boolean }[];
  setDisplayedColumns: (value: { key: string; displayed: boolean }[]) => void;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<number | null>(null);
  const currentMouseYRef = useRef<number>(0);

  class ColumnItem extends React.Component<
    ColumnItemProps<T>,
    { hovered: boolean }
  > {
    constructor(props: ColumnItemProps<T>) {
      super(props);
      this.state = { hovered: false };
    }

    handleSelect = (selected: boolean) => {
      const { item, commonProps } = this.props;
      commonProps.onSelect(selected, item.key);
    };

    handleHover = (hovered: boolean) => {
      this.setState({ hovered });
    };

    override render() {
      const { item } = this.props;
      const { hovered } = this.state;

      const showDragHandle = this.props.showDragHandle ?? true;
      const isDisplayed =
        displayedColumns.find((col) => col.key === item.key)?.displayed ??
        false;

      return (
        <Flex
          align="center"
          className={classnames(
            styles.columnItem,
            showDragHandle && hovered ? styles.columnItemDraggable : "",
          )}
          dir="row"
          onClick={() => {
            if (!isDisplayed) {
              this.handleSelect(true);
            }
          }}
          onMouseEnter={() => this.handleHover(true)}
          onMouseLeave={() => this.handleHover(false)}
          {...(showDragHandle ? this.props.dragHandleProps : {})}
        >
          <Flex dir="row" gap="xl" grow={1}>
            <div onClick={(e) => e.stopPropagation()}>
              <RedoCheckbox
                dataTestId={`column-selector-checkbox-${item.key}`}
                setValue={this.handleSelect}
                size="xs"
                value={isDisplayed}
              />
            </div>
            <Text fontSize="xs">{item.title}</Text>
          </Flex>
          {showDragHandle && (
            <div
              className={classnames(
                styles.dragHandle,
                hovered ? styles.dragHandleHovered : "",
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <DotsReorderIcon className={styles.icon} />
            </div>
          )}
        </Flex>
      );
    }
  }

  const StaticColumnItem = ({ column }: { column: Column2<T> }) => {
    const isDisplayed =
      displayedColumns.find((col) => col.key === column.key)?.displayed ??
      false;

    const handleSelect = (selected: boolean) => {
      const columnIndex = displayedColumns.findIndex(
        (c) => c.key === column.key,
      );
      if (columnIndex !== -1) {
        const updatedColumns = [...displayedColumns];
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        updatedColumns[columnIndex].displayed = selected === true;
        setDisplayedColumns(updatedColumns);
      }
    };

    return (
      <Flex
        align="center"
        className={styles.columnItem}
        dir="row"
        onClick={() => {
          if (!isDisplayed) {
            handleSelect(true);
          }
        }}
      >
        <Flex dir="row" gap="xl" grow={1}>
          <div onClick={(e) => e.stopPropagation()}>
            <RedoCheckbox
              dataTestId={`column-selector-checkbox-${column.key}`}
              setValue={handleSelect}
              size="xs"
              value={isDisplayed}
            />
          </div>
          <Text fontSize="xs">{column.title}</Text>
        </Flex>
      </Flex>
    );
  };

  const correctlySortedCols = useMemo(() => {
    const displayOrderMap = new Map(
      displayedColumns.map((col, index) => [col.key, index]),
    );
    return columns
      .sort((a, b) => {
        const orderA = displayOrderMap.get(a.key) ?? Infinity;
        const orderB = displayOrderMap.get(b.key) ?? Infinity;
        return orderA - orderB;
      })
      .filter((col) => !col.nonHidable && col.available !== false);
  }, [columns, displayedColumns]);

  const searcher = useMemo(
    () => new Fuse(correctlySortedCols, { keys: ["title"], threshold: 0.2 }),
    [correctlySortedCols],
  );

  const filteredColumns = useSearch(searcher, correctlySortedCols, searchText);

  const { selectedColumns, unselectedColumns } = useMemo(() => {
    const selected = correctlySortedCols.filter(
      (col) => displayedColumns.find((c) => c.key === col.key)?.displayed,
    );
    const unselected = correctlySortedCols
      .filter(
        (col) => !displayedColumns.find((c) => c.key === col.key)?.displayed,
      )
      .sort((a, b) => {
        const aTitle = typeof a.title === "string" ? a.title : "";
        const bTitle = typeof b.title === "string" ? b.title : "";
        return aTitle.localeCompare(bTitle);
      });
    return { selectedColumns: selected, unselectedColumns: unselected };
  }, [correctlySortedCols, displayedColumns]);

  const allSelected = useMemo(
    () =>
      correctlySortedCols.every(
        (col) => displayedColumns.find((c) => c.key === col.key)?.displayed,
      ),
    [correctlySortedCols, displayedColumns],
  );

  const handleSelectAll = useHandler(() => {
    const selectableKeys = new Set(correctlySortedCols.map((col) => col.key));
    setDisplayedColumns(
      displayedColumns.map((col) =>
        selectableKeys.has(col.key) ? { ...col, displayed: true } : col,
      ),
    );
  });

  const handleDeselectAll = useHandler(() => {
    const selectableKeys = new Set(correctlySortedCols.map((col) => col.key));
    setDisplayedColumns(
      displayedColumns.map((col) =>
        selectableKeys.has(col.key) ? { ...col, displayed: false } : col,
      ),
    );
  });

  const toggleDropdownOpen = useHandler(() => {
    setDropdownOpen((prev) => {
      const newOpen = !prev;
      return newOpen;
    });
    setSearchText("");
  });

  useEffect(() => {
    if (!dropdownButtonRef) {
      return;
    }
    dropdownButtonRef.addEventListener("click", toggleDropdownOpen);
    return () => {
      dropdownButtonRef.removeEventListener("click", toggleDropdownOpen);
    };
  }, [dropdownButtonRef, toggleDropdownOpen]);

  const handleMouseMove = useHandler((e: MouseEvent) => {
    currentMouseYRef.current = e.clientY;

    // Only auto-scroll if the primary mouse button is pressed (dragging)
    if (e.buttons !== 1) {
      if (autoScrollIntervalRef.current !== null) {
        cancelAnimationFrame(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      return;
    }

    const container = dropdownContainerRef.current;
    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const mouseY = e.clientY;
    const scrollThreshold = 50;
    const scrollSpeed = 10;

    const shouldScrollUp =
      mouseY < rect.top + scrollThreshold && container.scrollTop > 0;
    const shouldScrollDown =
      mouseY > rect.bottom - scrollThreshold &&
      container.scrollTop < container.scrollHeight - container.clientHeight;

    if (!shouldScrollUp && !shouldScrollDown) {
      if (autoScrollIntervalRef.current !== null) {
        cancelAnimationFrame(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      return;
    }

    if (autoScrollIntervalRef.current === null) {
      const scroll = () => {
        const container = dropdownContainerRef.current;
        if (!container) {
          return;
        }

        const rect = container.getBoundingClientRect();
        const mouseY = currentMouseYRef.current;

        if (mouseY < rect.top + scrollThreshold && container.scrollTop > 0) {
          container.scrollTop -= scrollSpeed;
          autoScrollIntervalRef.current = requestAnimationFrame(scroll);
        } else if (
          mouseY > rect.bottom - scrollThreshold &&
          container.scrollTop < container.scrollHeight - container.clientHeight
        ) {
          container.scrollTop += scrollSpeed;
          autoScrollIntervalRef.current = requestAnimationFrame(scroll);
        } else {
          autoScrollIntervalRef.current = null;
        }
      };

      autoScrollIntervalRef.current = requestAnimationFrame(scroll);
    }
  });

  const handleMouseUp = useHandler(() => {
    if (autoScrollIntervalRef.current !== null) {
      cancelAnimationFrame(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  });

  useEffect(() => {
    if (!dropdownOpen) {
      return;
    }

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (autoScrollIntervalRef.current !== null) {
        cancelAnimationFrame(autoScrollIntervalRef.current);
      }
    };
  }, [dropdownOpen, handleMouseMove, handleMouseUp]);

  return (
    <>
      {dropdownOpen && (
        <ClickAwayListener onClickAway={toggleDropdownOpen}>
          <Dropdown
            anchor={dropdownButtonRef}
            className={styles.dropdown}
            fitToAnchor={false}
            flexProps={{ p: "none", gap: "none" }}
            open
          >
            <Flex className={styles.dropdownInner} dir="column" gap="none">
              <Flex
                borderBottomWidth="1px"
                borderColor="primary"
                borderStyle="solid"
                className={styles.filterDropdownHeader}
                dir="column"
                gap="xs"
                px="lg"
                py="md"
              >
                <BaseRedoInput
                  autoFocus
                  placeholder="Search columns..."
                  setValue={setSearchText}
                  size="sm"
                  value={searchText}
                />
                <Flex dir="row" justify="flex-end" w="full">
                  <RedoButton
                    hierarchy="link"
                    onClick={allSelected ? handleDeselectAll : handleSelectAll}
                    size="xs"
                    text={allSelected ? "Deselect all" : "Select all"}
                  />
                </Flex>
              </Flex>
              <Flex
                className={styles.dropdownContent}
                dir="column"
                gap="none"
                px="lg"
                py="md"
                ref={dropdownContainerRef}
              >
                {searchText.trim().length > 0 ? (
                  // It doesn't make sense to reorder columns when filtered
                  filteredColumns.length === 0 ? (
                    <Flex align="center" dir="column" justify="center" py="md">
                      <Text fontSize="xs" textColor="tertiary">
                        No results
                      </Text>
                    </Flex>
                  ) : (
                    <Flex dir="column" gap="sm">
                      {filteredColumns.map((col) => (
                        <ColumnItem
                          commonProps={{
                            onSelect: (selected: boolean, key: string) => {
                              const columnIndex = displayedColumns.findIndex(
                                (c) => c.key === key,
                              );
                              if (columnIndex !== -1) {
                                const updatedColumns = [...displayedColumns];
                                // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                                updatedColumns[columnIndex].displayed =
                                  selected;
                                setDisplayedColumns(updatedColumns);
                              }
                            },
                          }}
                          dragHandleProps={{}}
                          item={col}
                          key={col.key}
                          showDragHandle={false}
                        />
                      ))}
                    </Flex>
                  )
                ) : (
                  <>
                    {selectedColumns.length > 0 && (
                      <DraggableList<
                        Column2<T>,
                        { onSelect: (selected: boolean, key: string) => void },
                        ColumnItem
                      >
                        commonProps={{
                          onSelect: (selected: boolean, key: string) => {
                            const columnIndex = displayedColumns.findIndex(
                              (col) => col.key === key,
                            );
                            if (columnIndex !== -1) {
                              const updatedColumns = [...displayedColumns];
                              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                              updatedColumns[columnIndex].displayed = selected;
                              setDisplayedColumns(updatedColumns);
                            }
                          },
                        }}
                        constrainDrag
                        container={() => dropdownContainerRef.current}
                        itemKey={(item: Column2<T>) => item.key}
                        list={selectedColumns}
                        onMoveEnd={(newList) => {
                          const newDisplayedColumns: {
                            key: string;
                            displayed: boolean;
                          }[] = [];
                          for (const item of newList) {
                            newDisplayedColumns.push({
                              key: item.key,
                              displayed: true,
                            });
                          }
                          for (const item of unselectedColumns) {
                            newDisplayedColumns.push({
                              key: item.key,
                              displayed: false,
                            });
                          }

                          for (const col of columns.reverse()) {
                            if (col.nonHidable) {
                              newDisplayedColumns.unshift({
                                key: col.key,
                                displayed: true,
                              });
                            }
                          }

                          setDisplayedColumns([...newDisplayedColumns]);
                        }}
                        padding={8}
                        template={ColumnItem}
                      />
                    )}

                    {selectedColumns.length > 0 &&
                      unselectedColumns.length > 0 && (
                        <Divider classes={styles.sectionDivider} />
                      )}

                    {unselectedColumns.length > 0 && (
                      <Flex dir="column" gap="sm">
                        {unselectedColumns.map((col) => (
                          <StaticColumnItem column={col} key={col.key} />
                        ))}
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
            </Flex>
          </Dropdown>
        </ClickAwayListener>
      )}
    </>
  );
});
